import { useEffect, useRef } from 'react';

import { trackEvent } from '@/lib/analytics';
import { AnalyticsEvent } from '@/types/analytics';

import type { createAppRouter } from '../router';

type AppRouterInstance = ReturnType<typeof createAppRouter>;
type AppRouterState = AppRouterInstance['state'];

const buildRoutePattern = (matches: AppRouterState['matches']) =>
  matches
    .map((match) => match.route.path)
    .filter((path): path is string => Boolean(path))
    .reduce((pattern, segment) => {
      if (segment.startsWith('/')) return segment;
      return `${pattern.replace(/\/$/, '')}/${segment}`;
    }, '');

export const usePageTracking = (router: AppRouterInstance) => {
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const trackPageView = (state: AppRouterState) => {
      const path = state.location.pathname;
      if (path === lastPath.current) return;
      lastPath.current = path;

      trackEvent(AnalyticsEvent.PAGE_VIEWED, {
        path,
        route: buildRoutePattern(state.matches),
      });
    };

    trackPageView(router.state);
    return router.subscribe(trackPageView);
  }, [router]);
};
